/**
 * EmployeePicker — Mitarbeiter-Auswahl (Combobox) statt roher employee_id-Textfelder.
 *
 * Spezifikation: RMNC-Leitstand/docs/UX_KONZEPT.md §2. WAI-ARIA-Combobox-Pattern
 * (role="combobox" am Input, role="listbox"/"option" an der Liste,
 * aria-activedescendant für die Tastatur-Hervorhebung).
 *
 * Datenquelle: GET /api/v1/employees?nur_aktive=false (existiert bereits,
 * backend/app/api/v1/employees.py) — EIN Request, clientseitig gefiltert
 * (React-Query, staleTime 5 min), kein Server-Roundtrip pro Tastendruck.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"

export interface EmployeeOption {
  id: string
  name: string
  kuerzel: string | null
  aktiv: boolean
}

export interface EmployeePickerProps {
  value: string | null
  onChange: (id: string | null, employee?: EmployeeOption) => void
  label?: string
  required?: boolean
  disabled?: boolean
}

export async function fetchAllEmployees(): Promise<EmployeeOption[]> {
  const resp = await api.get<EmployeeOption[]>("/employees", { params: { nur_aktive: false } })
  return resp.data
}

function anzeige(e: Pick<EmployeeOption, "name" | "kuerzel">): string {
  return e.kuerzel ? `${e.name} (${e.kuerzel})` : e.name
}

export function EmployeePicker({
  value,
  onChange,
  label = "Mitarbeiter",
  required = false,
  disabled = false,
}: EmployeePickerProps) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const [zeigeInaktive, setZeigeInaktive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const listboxId = useRef(`employee-picker-listbox-${Math.random().toString(36).slice(2)}`).current

  const { data: employees, isLoading, isError, refetch } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: fetchAllEmployees,
    staleTime: 5 * 60_000,
  })

  const selected = useMemo(
    () => employees?.find((e) => e.id === value) ?? null,
    [employees, value],
  )

  // Eingabefeld zeigt den gewählten Wert, solange die Liste geschlossen ist.
  useEffect(() => {
    if (!open) setQuery(selected ? anzeige(selected) : "")
  }, [selected, open])

  const gefiltert = useMemo(() => {
    if (!employees) return []
    const q = query.trim().toLocaleLowerCase("de")
    return employees
      .filter((e) => zeigeInaktive || e.aktiv || e.id === value)
      .filter((e) => {
        if (!q) return true
        return (
          e.name.toLocaleLowerCase("de").includes(q) ||
          (e.kuerzel ?? "").toLocaleLowerCase("de").includes(q)
        )
      })
  }, [employees, query, zeigeInaktive, value])

  useEffect(() => {
    if (activeIdx >= gefiltert.length) setActiveIdx(Math.max(0, gefiltert.length - 1))
  }, [gefiltert.length, activeIdx])

  // Schließen bei Klick außerhalb — stellt den letzten gültigen Wert wieder her.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery(selected ? anzeige(selected) : "")
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open, selected])

  function selectEmployee(e: EmployeeOption) {
    onChange(e.id, e)
    setQuery(anzeige(e))
    setOpen(false)
    inputRef.current?.blur()
  }

  function clear() {
    onChange(null)
    setQuery("")
    inputRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
      setActiveIdx((i) => Math.min(gefiltert.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const opt = gefiltert[activeIdx]
      if (opt) selectEmployee(opt)
    } else if (e.key === "Escape") {
      setOpen(false)
      setQuery(selected ? anzeige(selected) : "")
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <label className="mb-1 block text-sm text-slate-700">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={
            open && gefiltert[activeIdx] ? `${listboxId}-opt-${activeIdx}` : undefined
          }
          value={query}
          disabled={disabled}
          required={required}
          placeholder="Name oder Kürzel eingeben…"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            setActiveIdx(0)
          }}
          onKeyDown={handleKeyDown}
          className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60 disabled:bg-slate-100"
        />
        {value && !disabled && !required && (
          <button
            type="button"
            aria-label="Auswahl leeren"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            ✕
          </button>
        )}
      </div>

      {open && !disabled && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {isLoading && (
            <div className="animate-pulse space-y-1 p-2">
              <div className="h-6 rounded-md bg-slate-100" />
              <div className="h-6 rounded-md bg-slate-100" />
              <div className="h-6 rounded-md bg-slate-100" />
            </div>
          )}
          {isError && (
            <div className="p-3 text-sm text-red-700">
              Mitarbeiterliste konnte nicht geladen werden.
              <button
                type="button"
                onClick={() => refetch()}
                className="ml-2 rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Erneut versuchen
              </button>
            </div>
          )}
          {!isLoading && !isError && gefiltert.length === 0 && (
            <div className="p-3 text-sm text-slate-500">Keine Mitarbeiter gefunden.</div>
          )}
          {!isLoading &&
            !isError &&
            gefiltert.map((e, idx) => (
              <button
                key={e.id}
                id={`${listboxId}-opt-${idx}`}
                type="button"
                role="option"
                aria-selected={idx === activeIdx}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => selectEmployee(e)}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                  idx === activeIdx ? "bg-slate-100" : "hover:bg-slate-50"
                } ${!e.aktiv ? "text-slate-400" : "text-slate-900"}`}
              >
                <span>{anzeige(e)}</span>
                {!e.aktiv && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                    — inaktiv
                  </span>
                )}
              </button>
            ))}
          {!isLoading && !isError && (
            <label className="flex items-center gap-2 border-t border-slate-100 px-3 py-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={zeigeInaktive}
                onChange={(e) => setZeigeInaktive(e.target.checked)}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              />
              Inaktive anzeigen
            </label>
          )}
        </div>
      )}
    </div>
  )
}

/** Auflösung einer gespeicherten employee_id gegen die gecachte Liste (Listen/Detail). */
export function useEmployeeLabel(employeeId: string | null | undefined): string | null {
  const { data: employees } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: fetchAllEmployees,
    staleTime: 5 * 60_000,
    enabled: !!employeeId,
  })
  if (!employeeId) return null
  const found = employees?.find((e) => e.id === employeeId)
  return found ? anzeige(found) : null
}
